import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft } from "lucide-react";

const BatchGenerate = () => {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [generating, setGenerating] = useState(false);
  const [hairstyleInput, setHairstyleInput] = useState(`crew cut
buzz cut #2
French crop textured fringe
short afro shape up
twist sponge top + fade
comb over fade
slick back undercut
pompadour fade
quiff
curly top + taper
waves 360 + taper line-up`);
  const [category, setCategory] = useState("Classic");
  const [sex, setSex] = useState("male");

  const hairstyles = useMemo(() => {
    return hairstyleInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(name => ({
        name,
        category,
        sex
      }));
  }, [hairstyleInput, category, sex]);

  const handleBatchGenerate = async () => {
    if (hairstyles.length === 0) {
      toast({
        title: "No hairstyles",
        description: "Please add at least one hairstyle name",
        variant: "destructive",
      });
      return;
    }

    setGenerating(true);
    let totalSuccess = 0;
    let totalFailed = 0;
    const failedItems: string[] = [];
    
    try {
      toast({
        title: "Starting batch generation",
        description: `Processing ${hairstyles.length} hairstyles (one at a time to avoid timeouts)...`,
      });

      // Process one hairstyle at a time to avoid timeouts
      for (let i = 0; i < hairstyles.length; i++) {
        const hairstyle = hairstyles[i];
        
        toast({
          title: "Processing...",
          description: `Generating ${hairstyle.name} (${i + 1}/${hairstyles.length})...`,
        });

        try {
          const { data, error } = await supabase.functions.invoke('batch-generate-hairstyles', {
            body: { hairstyles: [hairstyle] }
          });

          if (error) throw error;

          const successCount = data.results.filter((r: any) => r.status === 'success').length;
          const errorCount = data.results.filter((r: any) => r.status === 'error').length;
          
          totalSuccess += successCount;
          totalFailed += errorCount;

          if (errorCount > 0) {
            const failed = data.results
              .filter((r: any) => r.status === 'error')
              .map((r: any) => `${r.name} (${r.ethnicity})`);
            failedItems.push(...failed);
          }

        } catch (error: any) {
          console.error(`Error generating ${hairstyle.name}:`, error);
          totalFailed += 5; // 5 ethnicities failed
          failedItems.push(`${hairstyle.name} (all ethnicities)`);
        }
      }

      toast({
        title: "Batch generation complete!",
        description: `Successfully generated ${totalSuccess} images. ${totalFailed > 0 ? `${totalFailed} failed.` : ''}`,
      });

      if (failedItems.length > 0) {
        toast({
          title: "Some generations failed",
          description: `Failed: ${failedItems.join(', ')}`,
          variant: "destructive",
        });
      }

    } catch (error: any) {
      console.error('Error in batch generation:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate hairstyles",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    navigate("/");
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/admin")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Admin
          </Button>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Batch Generate Hairstyles
          </h1>
          <div className="w-32" />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Generate All Hairstyles</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="hairstyles">Hairstyle Names (one per line)</Label>
                <Textarea
                  id="hairstyles"
                  value={hairstyleInput}
                  onChange={(e) => setHairstyleInput(e.target.value)}
                  placeholder="crew cut&#10;buzz cut #2&#10;pompadour fade"
                  className="min-h-[200px] font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Paste or type hairstyle names, one per line
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Default Category</Label>
                  <select
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="Classic">Classic</option>
                    <option value="Fade">Fade</option>
                    <option value="Undercut">Undercut</option>
                    <option value="Crop">Crop</option>
                    <option value="Buzz">Buzz</option>
                    <option value="Afro">Afro</option>
                    <option value="Taper">Taper</option>
                    <option value="Waves">Waves</option>
                    <option value="Short">Short</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sex">Sex</Label>
                  <select
                    id="sex"
                    value={sex}
                    onChange={(e) => setSex(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="unisex">Unisex</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="border-t pt-4 space-y-4">
              <div className="space-y-2">
                <h3 className="font-semibold">Generation Summary</h3>
                <p className="text-sm text-muted-foreground">
                  {hairstyles.length} hairstyles × 5 ethnicities = <span className="font-bold">{hairstyles.length * 5} total images</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Category: {category} | Sex: {sex}
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold">Hairstyles ({hairstyles.length}):</h3>
                <div className="max-h-[150px] overflow-y-auto">
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                    {hairstyles.map((h, i) => (
                      <li key={i}>{h.name}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold">Ethnicities:</h3>
                <p className="text-sm text-muted-foreground">
                  Caucasian, African, Asian, Hispanic, Middle Eastern
                </p>
              </div>
            </div>

            <Button 
              onClick={handleBatchGenerate} 
              disabled={generating}
              className="w-full"
              size="lg"
            >
              {generating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating {hairstyles.length * 5} images...
                </>
              ) : (
                `Generate All ${hairstyles.length * 5} Images`
              )}
            </Button>

            <p className="text-xs text-muted-foreground">
              This process will take approximately {Math.ceil(hairstyles.length * 5 * 1.5 / 60)} minutes to complete.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BatchGenerate;
